/**
 * **Tab-Reihenfolge** — in welcher Folge Tab durch die **Canvas-Elemente**
 * läuft. Siehe „Tab-Reihenfolge" in CONTEXT.md.
 *
 * Bewusst ohne React-Bezug, wie die übrigen `utils/`: die Rechnung soll ohne
 * gerenderten Canvas prüfbar sein.
 */

import type { MovingRect } from './canvasAlignment'

/** Ein Canvas-Element, wie es die Reihenfolge braucht: Lage plus Kennung. */
export interface TabOrderRect extends MovingRect {
  id: string
}

/**
 * Die Elemente in Lesereihenfolge: waagerecht weiter, und wenn die Zeile zu
 * Ende ist, in der nächsten weiter.
 *
 * **Eine vollständige Reihenfolge statt paarweiser Nachbarschaftssprünge.**
 * Die naheliegende Umsetzung wäre „nimm den nächsten rechts, sonst den nächsten
 * darunter". Sie hat zwei Fehler, die erst im Gebrauch auffallen:
 *
 *   • Elemente werden unerreichbar. Wer rechts nichts mehr hat und darunter
 *     auch nicht, ist Endstation — obwohl links unten noch etwas liegt.
 *   • ⇧Tab wäre nicht die Umkehrung. Der „nächste rechts" von B ist nicht
 *     zwangsläufig der, dessen „nächster rechts" B ist; man käme woanders
 *     heraus, als man hergekommen ist.
 *
 * Über eine Reihenfolge ist beides gelöst: Jedes Element kommt genau einmal
 * vor, und ein Schritt zurück ist buchstäblich der Schritt zurück.
 *
 * **Was eine Zeile ist:** alle Elemente, deren senkrechte Ausdehnung die des
 * obersten noch nicht eingeteilten Elements — der SAAT — überlappt.
 *
 * Überlappung und keine Pixel-Toleranz, weil eine Zahl wie „±40px gilt als
 * dieselbe Zeile" geraten wäre und kaputtginge, sobald jemand eine Notiz
 * größer zieht. Die Überlappung skaliert mit den Elementen selbst und trifft
 * genau das, was ein Mensch als „nebeneinander" liest.
 *
 * Die BERÜHRUNG zählt dabei nicht: Liegt die Unterkante von A genau auf der
 * Oberkante von B, stehen sie übereinander, nicht nebeneinander. Deshalb
 * strikt `<` und `>` — bewusst anders als `isVisible`, wo die Berührung als
 * sichtbar gilt. Dort geht es um Sichtbarkeit, hier um Nachbarschaft.
 *
 * Gemessen wird an der SAAT und nicht an einem mitwachsenden Verband: Sonst
 * zöge eine Treppe aus leicht versetzten Notizen beliebig weit mit und
 * verschmölze zu einer einzigen Zeile quer über den Canvas. Die Folge, offen
 * ausgesprochen: Eine lange Treppe zerfällt in mehrere Zeilen. Das ist die
 * richtige Seite des Kompromisses — eine Zeile, die man nicht mehr als Zeile
 * sieht, hilft niemandem.
 *
 * Die Kennung als letzter Vergleich hält die Reihenfolge stabil, auch wenn
 * zwei Notizen exakt aufeinander liegen.
 */
export function buildTabOrder(rects: TabOrderRect[]): string[] {
  const rest = [...rects].sort((a, b) => a.y - b.y || a.x - b.x || (a.id < b.id ? -1 : 1))
  const order: string[] = []

  while (rest.length > 0) {
    const seed = rest[0]
    const seedTop = seed.y
    const seedBottom = seed.y + seed.height

    const row: TabOrderRect[] = []
    for (let i = rest.length - 1; i >= 0; i--) {
      const rect = rest[i]
      if (rect.y < seedBottom && rect.y + rect.height > seedTop) {
        row.push(rect)
        rest.splice(i, 1)
      }
    }

    row.sort((a, b) => a.x - b.x || a.y - b.y || (a.id < b.id ? -1 : 1))
    for (const rect of row) order.push(rect.id)
  }

  return order
}

/**
 * Einen Schritt in der Reihenfolge — vorwärts (`1`) oder rückwärts (`-1`).
 *
 * **Läuft um:** Nach dem letzten Element kommt wieder das erste. Eine
 * Reihenfolge, die in eine Sackgasse führt, lässt den Benutzer raten, ob die
 * Taste kaputt ist oder der Canvas zu Ende.
 *
 * `null`, wenn die Reihenfolge leer ist oder `from` nicht darin vorkommt —
 * dann gibt es nichts zu tun, und der Aufrufer gibt die Taste weiter.
 */
export function stepTabOrder(
  order: string[],
  from: string,
  direction: 1 | -1,
): string | null {
  if (order.length === 0) return null
  const index = order.indexOf(from)
  if (index === -1) return null
  return order[(index + direction + order.length) % order.length]
}
